/**
 * WalteroChart - Branded ECharts Wrapper Component
 * 
 * A wrapper around ReactECharts that applies Waltero brand theming and provides
 * consistent chart styling across the dashboard. Includes performance optimizations
 * for large datasets and proper loading states.
 */

import React, { useMemo, forwardRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  generateEChartsOption, 
  getPerformanceConfig,
  WALTERO_CHART_COLORS,
  WALTERO_ECHARTS_THEME
} from '../../theme/chartTheme';
import WalteroTypography from './WalteroTypography';

/**
 * WalteroChart Component
 * 
 * @param {Object} props
 * @param {Object} props.option - ECharts option object
 * @param {Array} props.data - Chart data (used for performance optimization)
 * @param {string} props.title - Chart title
 * @param {boolean} props.loading - Loading state
 * @param {string} props.loadingText - Loading text to display
 * @param {number} props.height - Chart height in pixels
 * @param {string} props.width - Chart width (CSS value)
 * @param {Object} props.style - Additional styles for chart container
 * @param {boolean} props.autoResize - Enable auto resize (default: true)
 * @param {string} props.renderer - ECharts renderer ('canvas' or 'svg', default: 'canvas')
 * @param {boolean} props.notMerge - ECharts notMerge option (default: false)
 * @param {boolean} props.lazyUpdate - ECharts lazyUpdate option (default: true)
 * @param {Object} props.theme - Custom theme override
 * @param {Function} props.onChartReady - Callback when chart is ready
 * @param {Function} props.onEvents - ECharts event handlers
 * @param {Object} props.opts - Additional ECharts options
 */
const WalteroChart = forwardRef(({
  option = {},
  data = [],
  title,
  loading = false,
  loadingText = 'Loading chart data...',
  height = 400,
  width = '100%',
  style = {},
  autoResize = true,
  renderer = 'canvas',
  notMerge = false,
  lazyUpdate = true,
  theme,
  onChartReady,
  onEvents,
  opts = {},
  ...props
}, ref) => {
  const muiTheme = useTheme();

  // Calculate performance configuration based on data size
  const performanceConfig = useMemo(() => {
    const dataSize = Array.isArray(data) ? data.length : 
                    (option.series && Array.isArray(option.series[0]?.data)) ? option.series[0].data.length : 0;
    return getPerformanceConfig(dataSize);
  }, [data, option]);

  // Generate the final ECharts option with Waltero theming
  const chartOption = useMemo(() => {
    const baseOption = {
      ...option,
      ...performanceConfig
    };

    // Apply title if provided
    if (title) {
      baseOption.title = {
        text: title,
        left: 'center',
        top: 0,
        textStyle: {
          ...WALTERO_ECHARTS_THEME.title.textStyle,
          fontSize: 16
        },
        ...baseOption.title
      };
    }

    // Generate the themed option
    return generateEChartsOption(baseOption);
  }, [option, performanceConfig, title]);

  // Chart container styles
  const containerStyle = useMemo(() => ({
    height: `${height}px`,
    width,
    position: 'relative',
    backgroundColor: 'transparent',
    ...style
  }), [height, width, style]);

  // Chart options for ReactECharts
  const chartOpts = useMemo(() => ({
    renderer,
    height,
    width: width === '100%' ? undefined : width,
    ...opts
  }), [renderer, height, width, opts]);

  // Loading overlay component
  const LoadingOverlay = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: 1,
        zIndex: 10
      }}
    >
      <CircularProgress 
        size={32} 
        thickness={4}
        sx={{ 
          color: muiTheme.palette.secondary.main,
          mb: 2 
        }} 
      />
      <WalteroTypography variant="body2" color="secondary">
        {loadingText}
      </WalteroTypography>
    </Box>
  );

  return (
    <Box sx={containerStyle} {...props}>
      {loading && <LoadingOverlay />}
      
      <ReactECharts
        ref={ref}
        option={chartOption}
        style={{ 
          height: '100%', 
          width: '100%',
          opacity: loading ? 0.3 : 1,
          transition: 'opacity 0.2s ease-in-out'
        }}
        opts={chartOpts}
        theme={theme}
        notMerge={notMerge}
        lazyUpdate={lazyUpdate}
        onChartReady={onChartReady}
        onEvents={onEvents}
        autoResize={autoResize}
      />
    </Box>
  );
});

WalteroChart.displayName = 'WalteroChart';

/**
 * WalteroLineChart - Pre-configured line chart
 */
export const WalteroLineChart = forwardRef((props, ref) => {
  const lineOption = useMemo(() => ({
    xAxis: {
      type: 'category',
      boundaryGap: false
    },
    yAxis: {
      type: 'value'
    },
    series: [{
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: {
        width: 2
      },
      areaStyle: {
        opacity: 0.1
      },
      ...props.option?.series?.[0]
    }],
    ...props.option
  }), [props.option]);

  return <WalteroChart ref={ref} {...props} option={lineOption} />;
});

WalteroLineChart.displayName = 'WalteroLineChart';

/**
 * WalteroBarChart - Pre-configured bar chart
 */
export const WalteroBarChart = forwardRef((props, ref) => {
  const barOption = useMemo(() => ({
    xAxis: {
      type: 'category'
    },
    yAxis: {
      type: 'value'
    },
    series: [{
      type: 'bar',
      barWidth: '60%',
      itemStyle: {
        borderRadius: [4, 4, 0, 0]
      },
      ...props.option?.series?.[0]
    }],
    ...props.option
  }), [props.option]);

  return <WalteroChart ref={ref} {...props} option={barOption} />;
});

WalteroBarChart.displayName = 'WalteroBarChart';

/**
 * WalteroPieChart - Pre-configured pie chart
 */
export const WalteroPieChart = forwardRef((props, ref) => {
  const pieOption = useMemo(() => ({
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['50%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 8,
        borderColor: '#fff',
        borderWidth: 2
      },
      label: {
        show: false,
        position: 'center'
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      labelLine: {
        show: false
      },
      ...props.option?.series?.[0]
    }],
    ...props.option
  }), [props.option]);

  return <WalteroChart ref={ref} {...props} option={pieOption} />;
});

WalteroPieChart.displayName = 'WalteroPieChart';

/**
 * WalteroScatterChart - Pre-configured scatter chart
 */
export const WalteroScatterChart = forwardRef((props, ref) => {
  const scatterOption = useMemo(() => ({
    xAxis: {
      type: 'value',
      scale: true
    },
    yAxis: {
      type: 'value',
      scale: true
    },
    series: [{
      type: 'scatter',
      symbolSize: 8,
      ...props.option?.series?.[0]
    }],
    ...props.option
  }), [props.option]);

  return <WalteroChart ref={ref} {...props} option={scatterOption} />;
});

WalteroScatterChart.displayName = 'WalteroScatterChart';

export default WalteroChart;